import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Users, ShieldCheck, Sparkles, CalendarClock, CheckCircle2, XCircle, Eye,
  MessageSquare, Heart, PackageSearch,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const Stat = ({ label, value, icon: Icon, color }) => (
  <div className="rounded-2xl border border-foreground/10 bg-white p-4">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="mt-1 font-heading text-2xl font-extrabold tracking-tight">{value}</div>
      </div>
      <div className="grid h-9 w-9 place-items-center rounded-xl text-white" style={{ background: color }}>
        <Icon size={14} />
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [providers, setProviders] = useState([]);
  const [tab, setTab] = useState("submitted");

  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data));
  const loadProviders = (s) => api.get("/admin/providers", { params: { status_filter: s } }).then(({ data }) => setProviders(data));

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadProviders(tab); }, [tab]);

  const approve = async (id) => {
    try { await api.post(`/admin/providers/${id}/approve`); toast.success("Approved"); loadProviders(tab); loadStats(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection?") || "";
    try {
      const fd = new FormData(); fd.append("reason", reason);
      await api.post(`/admin/providers/${id}/reject`, fd);
      toast.success("Rejected");
      loadProviders(tab); loadStats();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Admin console</h1>
          <p className="mt-1 text-sm text-muted-foreground">Approve providers, monitor bookings.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/satisfaction" data-testid="satisfaction-link">
            <Button className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90">
              <Heart size={14} className="mr-1.5" /> Satisfaction dashboard
            </Button>
          </Link>
          <Link to="/admin/requests" data-testid="requests-link">
            <Button variant="outline" className="rounded-full">
              <PackageSearch size={14} className="mr-1.5" /> Sourcing requests
            </Button>
          </Link>
          <Link to="/admin/feedback" data-testid="feedback-link">
            <Button variant="outline" className="rounded-full">
              <MessageSquare size={14} className="mr-1.5" /> Feedback log
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Users" value={stats.users || 0} icon={Users} color="#0A0A0A" />
        <Stat label="Providers" value={stats.providers || 0} icon={Sparkles} color="#00E5FF" />
        <Stat label="Pending KYC" value={stats.pending_kyc || 0} icon={ShieldCheck} color="#FFD600" />
        <Stat label="Approved" value={stats.approved_providers || 0} icon={CheckCircle2} color="#16a34a" />
        <Stat label="Services" value={stats.services || 0} icon={Sparkles} color="#FF3B30" />
        <Stat label="Bookings" value={stats.bookings || 0} icon={CalendarClock} color="#0A0A0A" />
      </div>

      <div>
        <h2 className="font-heading text-xl font-bold tracking-tight">Providers</h2>
        <Tabs value={tab} onValueChange={setTab} className="mt-3">
          <TabsList>
            <TabsTrigger value="submitted" data-testid="atab-submitted">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="atab-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="atab-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="expired" data-testid="atab-expired">Expired</TabsTrigger>
            <TabsTrigger value="pending" data-testid="atab-nodocs">No docs</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-4 space-y-3">
            {providers.length === 0 ? (
              <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">Nothing here.</div>
            ) : providers.map((p) => (
              <div key={p.id} className="rounded-2xl border bg-white p-4" data-testid={`provider-row-${p.user_id}`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="font-heading text-base font-bold">{p.user?.name}</div>
                    <div className="text-xs text-muted-foreground">{p.user?.email} · {p.user?.phone || "—"}</div>
                    <div className="mt-1 text-xs">Area: <b>{p.service_area || "—"}</b> · Skills: {p.skills?.join(", ") || "—"}</div>
                    {p.bio && <div className="mt-1 text-xs text-muted-foreground">{p.bio}</div>}
                    <div className="mt-2 text-xs">
                      Aadhaar: <b>{p.kyc?.aadhaar_number || "—"}</b> · DL: <b>{p.kyc?.driving_license || "—"}</b> · Voter: <b>{p.kyc?.voter_id || "—"}</b>
                    </div>
                    {p.kyc?.certification && (p.kyc.certification.type || p.kyc.certification.number) && (
                      <div className="mt-1 rounded-xl bg-brand-orange/5 border border-brand-orange/20 p-2 text-xs">
                        <b className="uppercase text-[10px] tracking-widest text-brand-orange">Cert · {p.kyc.certification.type || "—"}</b>
                        <div>No: <b>{p.kyc.certification.number || "—"}</b> · Issued by: {p.kyc.certification.authority || "—"} · Exp: {p.kyc.certification.experience_years ?? "—"} yrs</div>
                        {p.kyc.certification.expires_at && (
                          <div className="mt-0.5">
                            Certificate expires: <b className={new Date(p.kyc.certification.expires_at) < new Date() ? "text-brand-orange" : ""}>{p.kyc.certification.expires_at}</b>
                            {new Date(p.kyc.certification.expires_at) < new Date() && <span className="ml-2 rounded-full bg-brand-orange px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">EXPIRED</span>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {p.kyc?.documents?.length > 0 && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="rounded-full" data-testid={`view-docs-${p.user_id}`}>
                            <Eye size={14} className="mr-1.5" /> View docs
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl">
                          <DialogHeader><DialogTitle>{p.user?.name} — KYC documents</DialogTitle></DialogHeader>
                          <div className="grid grid-cols-2 gap-3">
                            {p.kyc.documents.map((d, i) => (
                              <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${d}`} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-2xl border">
                                <img src={`${process.env.REACT_APP_BACKEND_URL}${d}`} className="h-64 w-full object-cover" alt="doc" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                <div className="p-2 text-xs">Open document {i + 1}</div>
                              </a>
                            ))}
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                    {p.kyc?.status !== "approved" && (
                      <Button className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90" onClick={() => approve(p.user_id)} data-testid={`approve-${p.user_id}`}>
                        <CheckCircle2 size={14} className="mr-1.5" /> Approve
                      </Button>
                    )}
                    {p.kyc?.status !== "rejected" && (
                      <Button variant="outline" className="rounded-full" onClick={() => reject(p.user_id)} data-testid={`reject-${p.user_id}`}>
                        <XCircle size={14} className="mr-1.5" /> Reject
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
